// React

import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

// @mui material components
import { Grid, Icon, InputLabel, MenuItem, Select } from "@mui/material";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { validateCUIL, validateDNI, validateEmail } from "utils/validations";
import ModalFinalizarProveedor from "../ModalFinalizarProveedor";
import ModalFotos from "../ModalFotos";
import ModalBienvenida from "../ModalBienvenida";
import { GoogleReCaptchaProvider, withGoogleReCaptcha } from "react-google-recaptcha-v3";

function CrearCuenta({ handleCancel, googleReCaptchaProps }) {
  const { executeRecaptcha } = googleReCaptchaProps;
  const [proveedor, setProveedor] = useState({
    tipoDocumento: "DNI",
  });
  const { handleSnackbar } = useContext(MessageManager);
  const [modalFotosOpen, setModalFotosOpen] = useState(false);
  const [modalFotosOpenAdj, setModalFotosOpenAdj] = useState(false);
  const [openModalFinalizar, setOpenModalFinalizar] = useState(false);
  const [openModalBienvenida, setOpenModalBienvenida] = useState(false);

  const [addProveedorUser, { loading: loadingSave, error: errorSaveUser }] = useMutation(gql`
    mutation addUserProveedor($input: UserConvenioInput!, $token: String) {
      saveUserConvenio(input: $input, token: $token) {
        id
      }
    }
  `);

  const { data: provincias } = useQuery(gql`
    query GetProvincias {
      provincias {
        id
        nombre
      }
    }
  `);

  const { data: dataConvenios } = useQuery(gql`
    query GetConvenios {
      convenios {
        id
        nombreInstitucion
      }
    }
  `);

  const [getLocalidades, { data: localidades }] = useLazyQuery(gql`
    query GetLocalidades($provincia: ID!) {
      localidades(provincia: $provincia) {
        id
        nombre
        provincia {
          id
        }
      }
    }
  `);

  const handleSave = async () => {
    const { provincia, localidad, ...rest } = proveedor;
    if (!executeRecaptcha) {
      handleSnackbar("Error al verificar el reCAPTCHA", "error");
      return;
    }

    const token = await executeRecaptcha("register_convenio");
    if (!token) {
      handleSnackbar("No se pudo obtener el token de reCAPTCHA", "error");
      return;
    }

    addProveedorUser({
      variables: {
        token,
        input: { ...rest, idLocalidad: rest.idLocalidad },
      },
    })
      .then((result) => {
        setOpenModalFinalizar(false);
        setOpenModalBienvenida(true);
      })
      .catch((error) => {
        console.log("error", error);
        handleSnackbar(error.message || "Error al agregar el usuario", "error");
      });
  };

  const handleChanges = (e) => {
    setProveedor({
      ...proveedor,
      [e.target.name]: e.target.value,
    });
  };

  const handleOpenModalFotos = () => {
    setModalFotosOpen(true);
  };

  const handleOpenModalFotosAdj = () => {
    setModalFotosOpenAdj(true);
  };

  useEffect(() => {
    if (proveedor?.provincia) {
      getLocalidades({ variables: { provincia: proveedor?.provincia } }).then((data) => {
        if (!data?.data?.localidades?.find((localidad) => localidad.id === proveedor?.localidad)) {
          setProveedor({
            ...proveedor,
            localidad: -1,
          });
        }
      });
    }
  }, [proveedor?.provincia]);

  return (
    <SoftBox p={3}>
      <SoftBox mb={2} display="flex" gap={1} flexDirection="column">
        <SoftTypography variant="h5">Registrarse en UPrO Comedor</SoftTypography>
        <SoftTypography variant="h7" fontWeight="regular">
          Formulario para convenios UNViMe e IFDC
        </SoftTypography>
      </SoftBox>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} lg={4}>
          <InputLabel htmlFor="nombre">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Nombre
              <SoftTypography variant="caption" fontWeight="bold" color="primary">
                &nbsp;*
              </SoftTypography>
            </SoftTypography>
          </InputLabel>
          <SoftInput
            label="nombre"
            placeholder="Ingrese nombre..."
            type="text"
            name="nombre"
            value={proveedor?.nombre || ""}
            onChange={handleChanges}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={4}>
          <InputLabel htmlFor="apellido">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Apellido
              <SoftTypography variant="caption" fontWeight="bold" color="primary">
                &nbsp;*
              </SoftTypography>
            </SoftTypography>
          </InputLabel>
          <SoftInput
            label="apellido"
            placeholder="Ingrese apellido..."
            type="text"
            name="apellido"
            value={proveedor?.apellido || ""}
            onChange={handleChanges}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={4}>
          <InputLabel htmlFor="usuario">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Correo electrónico
              <SoftTypography variant="caption" fontWeight="bold" color="primary">
                &nbsp;*&nbsp;
              </SoftTypography>
            </SoftTypography>
          </InputLabel>
          <SoftInput
            type="text"
            placeholder="Correo electrónico"
            name="email"
            value={proveedor.email}
            onChange={handleChanges}
            autoComplete="new-password"
            error={proveedor.email && proveedor.email.length > 0 && !validateEmail(proveedor.email)}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={4}>
          <InputLabel htmlFor="telefono">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Teléfono
            </SoftTypography>
          </InputLabel>
          <SoftInput
            label="telefono"
            placeholder="Ingrese teléfono..."
            type="text"
            name="telefono"
            value={proveedor?.telefono || ""}
            onChange={handleChanges}
            error={
              (proveedor?.telefono?.length > 0 && proveedor?.telefono?.length > 10) ||
              //Detecta si el telefono tiene letras
              proveedor?.telefono?.match(/[a-z]/i) ||
              //Detecta si el telefono tiene caracteres especiales
              proveedor?.telefono?.match(/[!@#$%^&*(),.?":{}|<>]/g) ||
              //Detecta si el telefono tiene espacios
              proveedor?.telefono?.match(/\s/g)
            }
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={4}>
          <InputLabel htmlFor="provincia">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Provincia
              <SoftTypography variant="caption" fontWeight="bold" color="primary"></SoftTypography>
            </SoftTypography>
          </InputLabel>
          <Select name="provincia" value={proveedor?.provincia || -1} onChange={handleChanges}>
            <MenuItem value={-1} disabled>
              Seleccione uno
            </MenuItem>
            {provincias?.provincias?.map((provincia) => (
              <MenuItem key={provincia.id} value={provincia.id}>
                {provincia.nombre}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={12} sm={6} lg={4}>
          <InputLabel htmlFor="localidad">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Localidad
              <SoftTypography variant="caption" fontWeight="bold" color="primary"></SoftTypography>
            </SoftTypography>
          </InputLabel>
          <Select name="idLocalidad" value={proveedor?.idLocalidad || -1} onChange={handleChanges}>
            <MenuItem value={-1} disabled>
              Seleccione uno
            </MenuItem>
            {localidades?.localidades?.map((localidad) => (
              <MenuItem key={localidad.id} value={localidad.id}>
                {localidad.nombre}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={12} sm={6} lg={4}>
          <InputLabel htmlFor="idConvenio">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Convenio
              <SoftTypography variant="caption" fontWeight="bold" color="primary">
                &nbsp;*
              </SoftTypography>
            </SoftTypography>
          </InputLabel>
          <Select name="idConvenio" value={proveedor?.idConvenio || -1} onChange={handleChanges}>
            <MenuItem value={-1} disabled>
              Seleccione un convenio
            </MenuItem>

            {dataConvenios?.convenios
              ?.filter((convenio) => convenio.id != 3)
              .map((convenio) => (
                <MenuItem key={convenio.id} value={convenio.id}>
                  {convenio.nombreInstitucion}
                </MenuItem>
              ))}
          </Select>
        </Grid>

        <Grid item xs={12} sm={6} lg={4}>
          <InputLabel htmlFor="dni">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              DNI
              <SoftTypography variant="caption" fontWeight="bold" color="primary">
                &nbsp;*
              </SoftTypography>
            </SoftTypography>
          </InputLabel>
          <SoftInput
            label="documento"
            placeholder="DNI (sin puntos ni guiones)"
            type="text"
            name="dni"
            value={proveedor?.dni || ""}
            error={
              proveedor?.tipoDocumento == "DNI" && proveedor?.dni?.length > 0
                ? !validateDNI(proveedor?.dni)
                : proveedor?.dni?.length > 0 && !validateCUIL(proveedor?.dni)
            }
            onChange={handleChanges}
          />
        </Grid>

        <Grid item xs={12} sm={4} lg={4}>
          <InputLabel htmlFor="tipoUsuario">
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              Tipo Usuario
              <SoftTypography variant="caption" fontWeight="bold" color="primary">
                &nbsp;*
              </SoftTypography>
            </SoftTypography>
          </InputLabel>
          <Select name="tipoUsuario" value={proveedor?.tipoUsuario || -1} onChange={handleChanges}>
            <MenuItem value={-1} disabled>
              Seleccione uno
            </MenuItem>

            <MenuItem value={"DOCENTE"}>DOCENTE</MenuItem>
            <MenuItem value={"ALUMNO"}>ALUMNO</MenuItem>
            <MenuItem value={"PERSONALAPOYO"}>PERSONAL DE APOYO</MenuItem>
          </Select>
        </Grid>

        <Grid item xs={12} md={6} lg={4} alignSelf={"center"}>
          <InputLabel htmlFor="tipoUsuario">
            <SoftTypography variant="caption" fontWeight="bold">
              Documentos
            </SoftTypography>
          </InputLabel>
          <SoftButton
            onClick={handleOpenModalFotos}
            color="primary"
            circular
            sx={{ width: { xs: "100%" } }}
          >
            <Icon sx={{ mr: 1 }}>upload_file</Icon> DNI
          </SoftButton>
        </Grid>

        <Grid item xs={12} md={6} lg={4} alignSelf={"center"}>
          <InputLabel htmlFor="tipoUsuario">
            <SoftTypography variant="caption" fontWeight="bold">
              Certificado
            </SoftTypography>
          </InputLabel>
          <SoftButton
            onClick={handleOpenModalFotosAdj}
            color="primary"
            circular
            disabled={!proveedor?.tipoUsuario}
            sx={{ width: { xs: "100%" } }}
          >
            <Icon sx={{ mr: 1 }}>upload_file</Icon>{" "}
            {proveedor?.tipoUsuario == "ALUMNO"
              ? "Certificadod de alumno regular"
              : "Certificado de prestación de servicios"}
          </SoftButton>
        </Grid>
      </Grid>
      <SoftBox
        mt={4}
        display="flex"
        flexDirection={{
          xs: "column",
          sm: "row",
        }}
        justifyContent={{
          xs: "center",
          sm: "flex-end",
        }}
        alignItems="center"
        gap={2}
      >
        <SoftBox sx={{ width: { xs: "100%", sm: "auto" } }}>
          <SoftButton type="submit" color="primary" fullWidth circular onClick={handleCancel}>
            <Icon sx={{ fontWeight: "light" }}>arrow_back</Icon>
            &nbsp; Cancelar
          </SoftButton>
        </SoftBox>
        <SoftBox sx={{ width: { xs: "100%", sm: "auto" } }}>
          <SoftButton
            type="submit"
            color="uproGreen"
            fullWidth
            circular
            disabled={
              loadingSave ||
              !proveedor.nombre ||
              !proveedor.apellido ||
              !proveedor.email ||
              !proveedor.idConvenio ||
              !proveedor.tipoDocumento ||
              !proveedor.dni ||
              !proveedor.tipoUsuario ||
              !proveedor?.documentosFile?.length > 0 ||
              !proveedor?.certificadoFile?.length > 0 || 
              (proveedor.email && !validateEmail(proveedor.email)) || 
              (proveedor.dni && !validateDNI(proveedor.dni))
            }
            onClick={() => {
              setOpenModalFinalizar(true);
            }}
          >
            Crear cuenta &nbsp;
            <Icon sx={{ fontWeight: "light" }}>arrow_forward</Icon>
          </SoftButton>
        </SoftBox>
      </SoftBox>
      <ModalFinalizarProveedor
        open={openModalFinalizar}
        handleClose={() => setOpenModalFinalizar(false)}
        handleSave={handleSave}
        loading={loadingSave}
      />
      <ModalFotos
        open={modalFotosOpen}
        handleClose={() => setModalFotosOpen(false)}
        proveedor={proveedor}
        handleSubmit={(files) => {
          setProveedor({ ...proveedor, documentosFile: files });
        }}
        esDNI={true}
      />
      <ModalFotos
        open={modalFotosOpenAdj}
        handleClose={() => setModalFotosOpenAdj(false)}
        proveedor={proveedor}
        handleSubmit={(files) => {
          setProveedor({ ...proveedor, certificadoFile: files });
        }}
        esDNI={false}
      />
      <ModalBienvenida
        open={openModalBienvenida}
        handleClose={() => {
          setOpenModalBienvenida(false);
          handleCancel();
        }}
      />
    </SoftBox>
  );
}

CrearCuenta.defaultProps = {
  handleCancel: () => {},
};

CrearCuenta.propTypes = {
  handleCancel: PropTypes.func,
  googleReCaptchaProps: PropTypes.object.isRequired,
};
const WrappedCrearCuentaProveedor = withGoogleReCaptcha(CrearCuenta);
function Capchat({ handleCancel }) {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_RECAPTCHA_KEY}>
      <WrappedCrearCuentaProveedor handleCancel={handleCancel} />
    </GoogleReCaptchaProvider>
  );
}

Capchat.propTypes = {
  handleCancel: PropTypes.func.isRequired,
};

export default Capchat;
